import {TableCell} from '@mui/material';
import type * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {ActualVsScheduled} from '@/components/Capacity/ActualVsScheduled';
import {useUpdateCapacityOverride} from '@/hooks/capacity/useUpdateCapacityOverride';
import type {EmployeeActuals} from '@/types/Actuals';
import type {CapacityDefault} from '@/types/CapacityDefault';
import type {Employee} from '@/types/Employee';

type Props = {
    labelId : number;
    capacityYearMonth : string;
    combinedHours : number;
    capacityDefaults ?: CapacityDefault[];
    employee : Employee;
    actual : EmployeeActuals | undefined;
};

export const cutoffDay = Number.parseInt(import.meta.env.VITE_APP_API_CAPACITY_CUTOFF_DAY);

const CapacityHours : React.FC<Props> = (
    {
        labelId,
        capacityYearMonth,
        combinedHours,
        employee,
        actual,
        capacityDefaults,
    } : Props
) => {
    const [hours, setHours] = useState<string>(combinedHours.toString());
    const useUpdateCapacityMutation = useUpdateCapacityOverride();
    const capacityDefault = useMemo(() => {
        return capacityDefaults?.find(d => d.timeLabelID === labelId)?.hours;
    }, [capacityDefaults, labelId]);
    const capacityYear = parseInt(capacityYearMonth.split('-')[0]);
    const capacityMonth = parseInt(capacityYearMonth.split('-')[1]);

    const disabled = useMemo(() => {
        const currentDate = new Date();

        if (labelId === 2) {
            return true;
        }

        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const currentDay = currentDate.getDate();
        const monthCutoff = currentDay >= cutoffDay ? currentMonth + 2 : currentMonth + 1;

        return (capacityYear === currentYear && monthCutoff >= capacityMonth)
            || (capacityYear < currentYear && monthCutoff >= capacityMonth);
    }, [capacityYear, capacityMonth, labelId]);

    const handleSave = (event : React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        let newHours = 0;

        if (event !== undefined) {
            newHours = event.target.value === '' ? 0 : Math.abs(parseFloat(event.target.value));

            useUpdateCapacityMutation.mutate({
                associateID: employee.id,
                timeLabelID: labelId,
                hours: newHours,
                capacityMonth: capacityYearMonth,
            });
        }
    };

    const handleChange = (event : React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        if (event !== undefined) {
            setHours(event.target.value === '' ? '0' : event.target.value);
        }
    };

    useEffect(() => {
        if (combinedHours.toString() !== hours) {
            setHours(combinedHours.toString());
        }
    }, [combinedHours]);

    return <TableCell
        align='right'
        sx={{
            textAlign: 'flex-end',
            backgroundColor: disabled ? '#eee' : 'transparent',
        }}
    >
        <form>
            <ActualVsScheduled
                scheduled={<input
                    type="text"
                    value={hours}
                    disabled={disabled}
                    onChange={handleChange}
                    onBlur={handleSave}
                    onFocus={e => {
                        e.target.select();
                    }}
                    style={{
                        color: 'inherit',
                        border: 'none',
                        backgroundColor: 'transparent',
                        margin: 0,
                        padding: 0,
                        paddingLeft: 20,
                        width: 50,
                        textAlign: 'right',
                        font: 'inherit',
                        verticalAlign: 'middle',
                    }}
                />}
                actual={actual}
                capacityYearMonth={capacityYearMonth}
                timeOff={labelId == 1}
                changedFromDefaults={capacityDefault !== undefined && capacityDefault !== combinedHours}
                capacityDefault={capacityDefault}
            />

        </form>
    </TableCell>;
};

export default CapacityHours;
